<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { parseSchedules } from 'src/helpers/actions/schedules/core/schedule_helpers';
	//Components
	import SchedulesList from '../../Schedules/SchedulesG2/SchedulesList.svelte';
	import CreateSchedulesG2 from '../../Schedules/SchedulesG2/CreateSchedulesG2.svelte';
	//Design system components
	import { Button, Alert, Loader } from '@Shelly/shelly-design-system';
	//Helpers
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	//Types
	import type { ScheduleParams, ScheduleRequestData } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	const deviceId = device.id;
	const mainDeviceId = real_id(device.id);
	$: mainSettingsStore = getDeviceSettings(mainDeviceId);

	let waitForResponse = false;
	let waitForSaveResponse = false;
	let scheduleRev = null;
	let schedules: ScheduleParams[] = [];
	let showWizard = false;
	let selectedSchedule: ScheduleParams = null;
	let scheduleListError = '';

	const getSchedules = async (forceRequest = false) => {
		waitForResponse = true;
		schedules = [];
		scheduleListError = '';
		const trv = getTrvComponentByDevice(device);
		scheduleRev = trv?.schedules?.schedule_rev;
		let res: SettingRequest;
		if (forceRequest || trv == null) {
			res = await callBluTRV(device, 'Trv.ListScheduleRules', { id: 0 }, false, false);
		}

		if (trv || res?.isok) {
			let jobs = [];
			if (Array.isArray(res?.data?.rules)) {
				jobs = res.data.rules;
			} else if (Array.isArray(res?.data)) {
				jobs = res.data;
			} else {
				jobs = trv?.schedules?.rules || [];
			}
			Object.values(jobs).forEach((schedule) => {
				const params = parseSchedules({
					id: schedule.rule_id,
					enable: schedule.enable,
					timespec: schedule.timespec,
					calls: [
						{
							method: schedule.target_C ? 'blutrv.target' : 'blutrv.position',
							params: { target_C: schedule.target_C, pos: schedule.pos },
						},
					],
				});
				schedules.push(params);
			});
		} else {
			scheduleListError = $format('alert_danger_sched_list_error', {
				default: "Could not load the schedule's list.",
			});
		}
		waitForResponse = false;
	};

	const createSchedule = (data: ScheduleParams = null) => {
		if (data == null) {
			const schedule: ScheduleRequestData = {
				enable: true,
				timespec: '0 0 0 * * *',
				calls: [],
			};
			data = parseSchedules(schedule);
		}
		selectedSchedule = data;
		showWizard = true;
	};

	const deleteAction = async (data: ScheduleParams) => {
		const res: SettingRequest = await callBluTRV(device, 'Trv.RemoveScheduleRule', {
			id: 0,
			rule_id: data.id,
		});
		if (res?.isok) {
			schedules = schedules.filter((i) => i.id != data.id);
		}
	};

	const saveAction = async (e: CustomEvent) => {
		waitForSaveResponse = true;
		const rule_id = e.detail.id;
		const data = e.detail.data;
		const params: any = {
			id: 0,
			rule: {
				enable: rule_id != null ? data.enable : true,
				timespec: data.timespec,
			},
		};
		if (rule_id != null) {
			params.rule_id = rule_id;
		}
		if (data.calls[0].method === 'blutrv.position') {
			params.rule.pos = data.calls[0].params.pos;
		} else {
			params.rule.target_C = data.calls[0].params.target_C;
		}

		const res: SettingRequest = await callBluTRV(
			device,
			rule_id != null ? 'Trv.UpdateScheduleRule' : 'Trv.AddScheduleRule',
			params,
		);
		if (res?.isok) {
			waitForResponse =
				rule_id == null ||
				JSON.stringify(e.detail.data) !== JSON.stringify(e.detail.old_schedule);
			showWizard = false;
		} else if (!res?.errors) {
			showWizard = false;
		}
		waitForSaveResponse = false;
	};

	const toggleAction = async (data: ScheduleParams) => {
		await callBluTRV(device, 'Trv.UpdateScheduleRule', {
			id: 0,
			rule_id: data.id,
			rule: {
				enable: data.enable,
			},
		});
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !waitForResponse && !scheduleListError) {
			stickyBottomAction.set({
				onClick: () => createSchedule(),
				loading: false,
				disabled: false,
				label: $format('button_add_schedule', { default: 'Add schedule' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	onMount(() => {
		getSchedules();
	});

	$: if (scheduleRev != null && $mainSettingsStore) {
		const component = getComponentByDevice(device);
		const trvID = $mainSettingsStore?.[component]?.id;
		const schedule_rev = $mainSettingsStore?.[`blutrv_rsched:${trvID}`]?.schedule_rev;
		if (schedule_rev != null && schedule_rev != scheduleRev) {
			scheduleRev = schedule_rev;
			getSchedules();
		}
	}
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if scheduleListError}
	<Alert type="danger" heading={scheduleListError} class="mb-s" />
	<Button
		size="m"
		class="width-full mr-s text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={() => getSchedules(true)}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else}
	{#if !schedules.length}
		<Alert
			class={classMap({
				'mb-s': schedules?.length,
			})}
			heading={$format('alert_heading_no_schedules', {
				default: 'No schedules registered',
			})}
			message={$format('alert_message_first_add_schedule', {
				default: 'If you want to use this feature, you first need to add a schedule.',
			})}
			isHeadingBold
		/>
	{:else}
		<SchedulesList
			list={schedules}
			{deviceId}
			on:edit={(e) => createSchedule(e.detail)}
			on:delete={(e) => deleteAction(e.detail)}
			on:toggle={(e) => {
				toggleAction(e.detail);
			}}
		/>
	{/if}

	{#if !$showNewLayout}
		<div class="pt-s">
			<Button size="m" width="full" on:click={() => createSchedule()}>
				{$format('button_add_schedule', {
					default: 'Add schedule',
				})}
			</Button>
		</div>
	{/if}
{/if}

{#if showWizard}
	<CreateSchedulesG2
		{device}
		schedule={selectedSchedule}
		waitForResponse={waitForSaveResponse}
		bind:showWizard
		on:save={saveAction}
	/>
{/if}
